import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Autorenew as AutorenewIcon,
} from "@mui/icons-material";
import {
  Formik,
  Form,
  FieldArray,
  FormikErrors,
  FormikTouched,
  FormikProps,
} from "formik";
import * as Yup from "yup";
import {
  ServiceLog,
  ServiceLogFormData,
  ServiceExpense,
  GarageDetails,
} from "../serviceLog";
import { scheduleService, serviceLogService } from "../serviceLogService";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
} from "../../../../utils/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../../components/Toast";
import NavigationHeader from "../../../../components/NavigationHeader";
import axios from "axios";
import { getValidToken } from "../../../../utils/authUtils";
import BasicVehicleDetailsView from "../../../../components/BasicVehicleDetailsView";
import { DocumentUploadSection } from "../../../../components/DocumentUploadSection";
import { uploadFile } from "../../../../utils/UploadFile";
import { fetchDocuments } from "../../../../utils/FetchDocuments";
import { masterDataAllocationApi } from "../../../../project/project_module/services/apiService";

const validationSchema = Yup.object().shape({
  serviceLogData: Yup.object().shape({
    serviceDate: Yup.date().required("Service date is required"),
    odometer: Yup.number()
      .required("Odometer reading is required")
      .min(0, "Must be a positive number")
      .max(999, "Must be less than 999"),
    garageDetails: Yup.object().shape({
      id: Yup.string().optional(),
      name: Yup.string().required("Garage name is required"),
      contactName: Yup.string().required("Garage contact name is required"),
      contactNumber: Yup.string().required("Garage contact number is required"),
      location: Yup.string().required("Garage location is required"),
    }),
    serviceType: Yup.string().required("Service type is required"),
    durationSinceLastService: Yup.number()
      .min(0, "Must be a positive number")
      .max(999, "Must be less than 999"),
    remarks: Yup.string(),
    amount: Yup.number()
      .required("Amount is required")
      .min(0, "Must be a positive number"),
    gst: Yup.number(),
    totalAmount: Yup.number()
      .required("Total amount is required")
      .min(0, "Must be a positive number"),
  }),
  serviceExpenseData: Yup.array()
    .of(
      Yup.object().shape({
        item: Yup.string().required("Item is required"),
        category: Yup.string().required("Category is required"),
        action: Yup.string().required("Action is required"),
        quantity: Yup.number()
          .required("Quantity is required")
          .min(1, "Must be at least 1"),
        rate: Yup.number()
          .required("Rate is required")
          .min(0.01, "Must be a positive number"),
        amount: Yup.number()
          .required("Amount is required")
          .min(0.01, "Must be a positive number"),
        gst: Yup.number(),
      })
    )
});

const initialExpense = {
  item: "",
  category: "",
  action: "",
  quantity: 1,
  rate: 0,
  amount: 0,
  gst: 0,
};

const initialValues: ServiceLogFormData = {
  serviceLogData: {
    vehicleId: "",
    serviceDate: new Date().toISOString().split("T")[0],
    odometer: 0,
    garageDetails: {
      name: "",
      contactName: "",
      contactNumber: "",
      location: "",
    },
    serviceType: "FREE",
    durationSinceLastService: 0,
    remarks: "",
    amount: 0,
    gst: 0,
    totalAmount: 0,
  },
  serviceExpenseData: [initialExpense],
};

interface FormValues {
  values: ServiceLogFormData;
  errors: FormikErrors<ServiceLogFormData>;
  touched: FormikTouched<ServiceLogFormData>;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(
      field: T
    ): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

// Add useDebounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface ServiceExpenseResponse {
  id: string;
  item: string;
  category: string;
  action: string;
  quantity: number;
  rate: string;
  amount: string;
  gst: string;
}

// Add these type definitions near the top of the file, after the imports
type ServiceExpenseErrors = {
  [K in keyof ServiceExpense]?: string;
};

type ServiceLogFormErrors = {
  serviceLogData?: FormikErrors<ServiceLogFormData["serviceLogData"]>;
  serviceExpenseData?: ServiceExpenseErrors[];
};

type ServiceLogFormTouched = {
  serviceLogData?: FormikTouched<ServiceLogFormData["serviceLogData"]>;
  serviceExpenseData?: { [K in keyof ServiceExpense]?: boolean }[];
};

// Add this type near your other type definitions
type ServiceLogFormProps = {
  values: ServiceLogFormData;
  errors: ServiceLogFormErrors;
  touched: ServiceLogFormTouched;
  handleChange: FormikProps<ServiceLogFormData>["handleChange"];
  setFieldValue: FormikProps<ServiceLogFormData>["setFieldValue"];
  onCancel: () => void;
  formikProps: FormikProps<ServiceLogFormData>;
  isSubmitting: boolean;
  isEditMode: boolean;
  selectedGarage: GarageDetails | null;
  setSelectedGarage: (garage: GarageDetails | null) => void;
  vehicleDetails: any;
  selectedFiles: File[];
  uploadedFiles: any[];
  onFileSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onPreview: (url: string) => void;
  isUploading: boolean;
};

const AddServiceLog: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { vehicleId, model, logId } = useParams<{
    vehicleId: string;
    model: string;
    logId?: string;
  }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] =
    useState<ServiceLogFormData>(initialValues);
  const [selectedGarage, setSelectedGarage] = useState<GarageDetails | null>(
    null
  );
  const [vehicleDetails, setVehicleDetails] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : "";

  useEffect(() => {
    if (vehicleId) {
      fetchVehicleDetails(vehicleId);
    }
  }, [vehicleId]);

  const fetchVehicleDetails = useCallback(
    async (vehicleId: string) => {
      try {
        const token = await getValidToken();

        const url = `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data) {
          setVehicleDetails(response.data.data || null);
        }
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
        showToast("Failed to load vehicle details", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [vehicleId, showToast]
  );

  const fetchServiceLog = useCallback(async (): Promise<void> => {
    if (!logId) return;

    try {
      setIsLoading(true);
      const response = await serviceLogService.getServiceLogById(logId);
      if (response?.isSuccess && response.data) {
        const serviceLog = response.data;
        setInitialFormData({
          serviceLogData: {
            vehicleId: serviceLog.vehicleId,
            serviceDate: new Date(serviceLog.serviceDate)
              .toISOString()
              .split("T")[0],
            odometer: Number(serviceLog.odometer),
            garageDetails: {
              id: serviceLog.garageDetails?.id || undefined,
              name: serviceLog.garageDetails.name,
              contactName: serviceLog.garageDetails.contactName,
              contactNumber: serviceLog.garageDetails.contactNumber,
              location: serviceLog.garageDetails.location,
            },
            serviceType: serviceLog.serviceType,
            durationSinceLastService: Number(
              serviceLog.durationSinceLastService
            ),
            remarks: serviceLog.remarks || "",
            amount: Number(serviceLog.amount),
            gst: Number(serviceLog.gst),
            totalAmount: Number(serviceLog.totalAmount),
          },
          serviceExpenseData: serviceLog.expenses?.map(
            (expense: ServiceExpenseResponse) => ({
              item: expense.item,
              category: expense.category,
              action: expense.action,
              quantity: Number(expense.quantity),
              rate: Number(expense.rate),
              amount: Number(expense.amount),
              gst: Number(expense.gst),
            })
          ) || [initialExpense],
        });

        if (serviceLog.garageDetails) {
          setSelectedGarage({
            id: serviceLog.garageDetails.id,
            name: serviceLog.garageDetails.name,
            contactName: serviceLog.garageDetails.contactName,
            contactNumber: serviceLog.garageDetails.contactNumber,
            location: serviceLog.garageDetails.location,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching service log:", error);
      showToast("Failed to load service log data", "error");
      navigate(`/vehicles/${vehicleId}/maintenance/${model}`);
    } finally {
      setIsLoading(false);
    }
  }, [logId, vehicleId, model, navigate, showToast]);

  // Fetch existing service log data if in edit mode
  useEffect(() => {
    fetchServiceLog();
  }, [logId]);

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const uploadServiceDocuments = async (serviceLogId: string) => {
    if (selectedFiles.length === 0) return;

    try {
      const uploadPromises = selectedFiles.map((file) =>
        uploadFile({
          file,
          documentType: "service-documents",
          documentTypeId: serviceLogId,
          documentContext: "service-log",
          description: "Service Log Documents",
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      showToast("Service documents uploaded successfully", "success");
      setSelectedFiles([]);
      await fetchServiceDocuments(serviceLogId);
    } catch (error) {
      console.error("Error uploading service documents:", error);
      showToast("Failed to upload service documents", "error");
    }
  };

  const fetchServiceDocuments = async (serviceLogId: string) => {
    try {
      const documents = await fetchDocuments({
        documentContext: "service-log",
        documentTypeId: serviceLogId,
        documentType: "service-documents",
      });

      setUploadedFiles(
        documents.map((doc: any) => ({
          id: doc.id,
          fileName: doc.fileName,
          presignedUrl: doc.presignedUrl,
        }))
      );
    } catch (error) {
      console.error("Error fetching service documents:", error);
      showToast("Failed to fetch service documents", "error");
    }
  };

  useEffect(() => {
    if (logId) {
      fetchServiceDocuments(logId);
    }
  }, [logId]);

  const handleSubmit = async (values: ServiceLogFormData) => {
    try {
      setIsSubmitting(true);
      values.serviceLogData.vehicleId = vehicleId || "";
      let response;
      if (logId) {
        response = await serviceLogService.updateServiceLog(logId, values);
        showToast("Service log updated successfully", "success");
      } else {
        response = await serviceLogService.createServiceLog(values);
        showToast("Service log created successfully", "success");
      }

      // Upload documents if any are selected
      if (selectedFiles.length > 0) {
        await uploadServiceDocuments(
          logId ? logId : response?.data?.serviceLog?.id || response?.data?.id
        );
      }

      navigate(`/vehicles/${vehicleId}/maintenance/${model}`);
    } catch (error) {
      showToast(
        logId ? "Failed to update service log" : "Failed to save service log",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/vehicles/${vehicleId}/maintenance/${model}`);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          {
            label: "Vehicle Maintenance",
            path: `/vehicles/${vehicleId}/maintenance/${model}`,
          },
          { label: logId ? "Edit Service Log" : "Add Service Log" },
        ]}
      />
      <BasicVehicleDetailsView vehicle={vehicleDetails} small={true} />
      <Paper sx={{ p: 3 }}>
        <Formik<ServiceLogFormData>
          initialValues={initialFormData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formikProps: FormikProps<ServiceLogFormData>) => (
            <ServiceLogForm
              {...(formikProps as unknown as ServiceLogFormProps)}
              onCancel={handleBack}
              formikProps={formikProps}
              isSubmitting={isSubmitting}
              isEditMode={!!logId}
              selectedGarage={selectedGarage}
              setSelectedGarage={setSelectedGarage}
              vehicleDetails={vehicleDetails}
              selectedFiles={selectedFiles}
              uploadedFiles={uploadedFiles}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              onPreview={handlePreview}
              isUploading={isUploading}
            />
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default AddServiceLog;

type ServiceExpenseFormData = ServiceExpense;

const ServiceLogForm: React.FC<ServiceLogFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  onCancel,
  formikProps,
  isSubmitting,
  isEditMode,
  selectedGarage,
  setSelectedGarage,
  selectedFiles,
  uploadedFiles,
  onFileSelect,
  onFileRemove,
  onPreview,
  isUploading,
}) => {
  const { projectId } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [garages, setGarages] = useState<GarageDetails[]>([]);
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const { model } = useParams<{ model: string }>();
  const { showToast } = useToast();
  const [scheduleData, setScheduleData] = useState<any>(null);
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Search garages
  const searchGarages = useCallback(async (term: string) => {
    if (term.length < 2 && term.length > 0) return;

    setIsLoading(true);
    setSearchError("");

    try {
      const data =
        await masterDataAllocationApi.getMasterDataAllocationsByProject(
          projectId!,
          "garage"
        );
      setGarages(data?.map((garage) => garage.masterData) as GarageDetails[]);
    } catch (error) {
      setSearchError("Failed to load garage data");
      console.error("Error searching garages:", error);
      setGarages([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to search when debounced term changes
  useEffect(() => {
    searchGarages(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchGarages]);

  // Handle garage selection
  const handleGarageChange = (
    garage: GarageDetails | null,
    inputValue?: string
  ) => {
    setSelectedGarage(garage);

    if (garage) {
      // Auto-fill fields from master data
      setIsManualEntry(false);
      setFieldValue("serviceLogData.garageDetails", {
        id: garage.id,
        name: garage.name,
        contactName: garage.contactName,
        contactNumber: garage.contactNumber,
        location: garage.location,
      });
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode
      setIsManualEntry(true);
      setFieldValue("serviceLogData.garageDetails", {
        name: inputValue.trim(),
        contactName: "",
        contactNumber: "",
        location: "",
      });
    } else {
      // Clear fields
      setIsManualEntry(false);
      setFieldValue("serviceLogData.garageDetails", {
        name: "",
        contactName: "",
        contactNumber: "",
        location: "",
      });
    }
  };

  // Get option label for autocomplete
  const getOptionLabel = (option: string | GarageDetails) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${option.contactNumber}`;
  };

  // Render garage option
  const renderGarageOption = (props: any, option: GarageDetails) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", mt: 0.25 }}
        >
          {option.location}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}
        >
          Contact: {option.contactNumber}
        </Typography>
        <Chip
          label={`Contact Person: ${option.contactName}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mt: 0.5, fontSize: "0.75rem" }}
        />
      </Box>
    </Box>
  );

  // Calculate expense totals
  const expenseTotals = useMemo(() => {
    return values.serviceExpenseData.reduce(
      (acc, expense) => {
        const amount = expense.quantity * expense.rate || 0;
        const gstPercentage = expense.gst || 0;
        const gstAmount = (amount * gstPercentage) / 100;
        return {
          amount: acc.amount + amount,
          gst: acc.gst + gstAmount,
          total: acc.total + amount + gstAmount,
        };
      },
      { amount: 0, gst: 0, total: 0 }
    );
  }, [values.serviceExpenseData]);

  // Update service log totals when expenses change
  useEffect(() => {
    setFieldValue("serviceLogData.amount", expenseTotals.amount);
    setFieldValue("serviceLogData.gst", expenseTotals.gst);
    setFieldValue("serviceLogData.totalAmount", expenseTotals.total);
  }, [expenseTotals, setFieldValue]);

  // Calculate expense amount and GST when values change
  const handleExpenseChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    handleChange(e);

    // Get the current expense
    const expense = values.serviceExpenseData[index];

    // If quantity or rate changed, update amount and recalculate GST amount
    if (name.includes("quantity") || name.includes("rate")) {
      const quantity = name.includes("quantity")
        ? Number(value.replace(/,/g, ""))
        : expense.quantity;
      const rate = name.includes("rate")
        ? Number(value.replace(/,/g, ""))
        : expense.rate;
      const amount = quantity * rate;
      setFieldValue(`serviceExpenseData.${index}.amount`, amount);

      // Recalculate GST amount based on percentage
      const gstPercentage = expense.gst || 0;
      const gstAmount = (amount * gstPercentage) / 100;
      setFieldValue(`serviceExpenseData.${index}.gstAmount`, gstAmount);
    }

    // If GST percentage changed, recalculate GST amount
    if (name.includes("gst")) {
      const gstPercentage = Number(value.replace(/,/g, ""));
      const amount = expense.amount || 0;
      const gstAmount = (amount * gstPercentage) / 100;
      setFieldValue(`serviceExpenseData.${index}.gstAmount`, gstAmount);
    }
  };

  const mapScheduleToExpenses = (data: any) => {
    // Combine all items from different categories into a single array
    const allScheduleItems = [
      ...Object.entries(data).reduce((acc: any[], [category, items]) => {
        // Skip non-array properties
        if (!Array.isArray(items)) return acc;

        // Map items with their category
        const categoryItems = items.map((item) => ({
          item: item.item,
          category: category,
          action: item.action,
          remarks: item.remarks,
          intervalKms: item.intervalKms,
        }));

        return [...acc, ...categoryItems];
      }, []),
    ];

    // Map to service expense format
    return allScheduleItems.map((item) => ({
      item: item.item || "",
      category: item.category || "",
      action: item.action || "",
      quantity: 1,
      rate: 0,
      amount: 0,
      gst: 0,
    }));
  };

  const fetchScheduleItems = async (odometer: number) => {
    try {
      if (values.serviceExpenseData.length > 1) return;
      if (!model || isEditMode) return;

      const response = await scheduleService.getScheduleByVehicleAndOdometer(
        model,
        odometer
      );

      if (response.isSuccess && response.data) {
        setScheduleData(response.data);

        // If within range, prefill immediately
        if (response.data.isWithinRange) {
          const scheduleExpenses = mapScheduleToExpenses(response.data);
          if (scheduleExpenses.length > 0) {
            setFieldValue("serviceExpenseData", scheduleExpenses);
          }

          // Set duration since last service if available
          if (response.data.intervalPeriod) {
            setFieldValue(
              "serviceLogData.durationSinceLastService",
              typeof response.data.intervalPeriod === "string"
                ? parseInt(response.data.intervalPeriod)
                : response.data.intervalPeriod
            );
          }

          showToast(
            `Found ${scheduleExpenses.length} scheduled maintenance items for ${response.data.dueOnKms}KMs service`,
            "info"
          );
        } else {
          // Show toast about available schedule
          showToast(
            response.message ||
              `Found closest service schedule ${response.data.kmsDifference}km away from requested value`,
            "info"
          );
        }
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
      showToast("Failed to fetch service schedule", "error");
    }
  };

  const handleLoadSchedule = () => {
    if (!scheduleData) return;

    const scheduleExpenses = mapScheduleToExpenses(scheduleData);
    if (scheduleExpenses.length > 0) {
      setFieldValue("serviceExpenseData", scheduleExpenses);
    }

    showToast(
      `Loaded ${scheduleExpenses.length} maintenance items for ${scheduleData.dueOnKms}KMs service`,
      "success"
    );
  };

  // Add onBlur handler for odometer field
  const handleOdometerBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const odometerValue = Number(e.target.value);
    if (odometerValue > 0) {
      fetchScheduleItems(odometerValue);
    }
  };

  return (
    <Form>
      <Box>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={3}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "1rem", fontWeight: 600 }}
            >
              Service Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Grid item xs={12} sm={3}>
              <TextField
                {...commonTextFieldProps}
                fullWidth
                type="number"
                name="serviceLogData.durationSinceLastService"
                label="Months Since Last Service"
                value={values.serviceLogData.durationSinceLastService || ""}
                onChange={handleChange}
                inputProps={{ maxLength: 3, type: "numeric" }}
                error={Boolean(
                  touched.serviceLogData?.durationSinceLastService &&
                    errors.serviceLogData?.durationSinceLastService
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "12px" }}>Service Type</InputLabel>
              <Select
                name="serviceLogData.serviceType"
                value={values.serviceLogData.serviceType}
                label="Service Type"
                onChange={handleChange}
                input={<OutlinedInput label="Service Type" />}
                sx={{ fontSize: "12px" }}
              >
                <MenuItem value="FREE" sx={{ fontSize: "12px" }}>
                  Free Service
                </MenuItem>
                <MenuItem value="PAID" sx={{ fontSize: "12px" }}>
                  Paid Service
                </MenuItem>
                <MenuItem value="OTHER" sx={{ fontSize: "12px" }}>
                  Other
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              type="date"
              fullWidth
              name="serviceLogData.serviceDate"
              label="Service Date"
              value={values.serviceLogData.serviceDate}
              onChange={handleChange}
              {...commonDateFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={1.5}>
            <TextField
              {...commonTextFieldProps}
              fullWidth
              type="number"
              name="serviceLogData.odometer"
              label="KMs Driven"
              value={values.serviceLogData.odometer}
              onChange={handleChange}
              onBlur={handleOdometerBlur}
              error={Boolean(
                touched.serviceLogData?.odometer &&
                  errors.serviceLogData?.odometer
              )}
              helperText={
                touched.serviceLogData?.odometer &&
                errors.serviceLogData?.odometer
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Garage Selection */}
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={garages}
              loading={isLoading}
              getOptionLabel={getOptionLabel}
              value={selectedGarage}
              onChange={(_, newValue, reason) => {
                if (reason === "selectOption" && typeof newValue === "object") {
                  handleGarageChange(newValue);
                } else if (reason === "clear") {
                  handleGarageChange(null);
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                setSearchTerm(inputValue);

                if (reason === "input") {
                  const matchingOption = garages.find((option) =>
                    getOptionLabel(option)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  );

                  if (!matchingOption && inputValue.trim() !== "") {
                    handleGarageChange(null, inputValue);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Garage"
                  placeholder="Search from master data or enter manually..."
                  required
                  fullWidth
                  // helperText={searchError ? searchError : 'Select from dropdown or type to add new garage'}
                  error={!!searchError}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  {...commonTextFieldProps}
                />
              )}
              renderOption={renderGarageOption}
              noOptionsText={
                isLoading
                  ? "Searching..."
                  : searchError
                  ? "Error loading options"
                  : "No matching garage found. You can still type to add manually."
              }
            />
          </Grid>

          {/* Manual Entry Alert */}
          <Grid container spacing={2} sx={{ mt: 0, ml: 0.5 }}>
            {isManualEntry && (
              <Grid item xs={6}>
                <Alert severity="info" sx={{ mb: 1, fontSize: "0.75rem" }}>
                  <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                    <strong>Manual Entry Mode:</strong> This garage is not in
                    our master data. Please fill in the details manually below.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
          {/* Auto-filled Garage Details */}
          <Grid container spacing={2} sx={{ mt: 0, ml: 0.5 }}>
            {selectedGarage && selectedGarage.id && !isManualEntry && (
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "primary.light",
                    bgcolor: "#F0F7FF",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary.dark"
                    gutterBottom
                  >
                    Garage Details (From Master Data)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Name: {selectedGarage.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Contact: {selectedGarage.contactNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Location: {selectedGarage.location}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Contact Person: {selectedGarage.contactName}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Manual Entry Fields */}
          {(isManualEntry || !selectedGarage?.id) && (
            <>
              <Grid item xs={12} sm={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  name="serviceLogData.garageDetails.contactName"
                  label="Contact Person Name"
                  value={values.serviceLogData.garageDetails.contactName}
                  onChange={handleChange}
                  error={Boolean(
                    touched.serviceLogData?.garageDetails?.contactName &&
                      errors.serviceLogData?.garageDetails?.contactName
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  name="serviceLogData.garageDetails.contactNumber"
                  label="Contact Number"
                  inputProps={{
                    maxLength: 10,
                  }}
                  placeholder="Enter 10 digit number"
                  value={values.serviceLogData.garageDetails.contactNumber}
                  onChange={handleChange}
                  error={Boolean(
                    touched.serviceLogData?.garageDetails?.contactNumber &&
                      errors.serviceLogData?.garageDetails?.contactNumber
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  name="serviceLogData.garageDetails.location"
                  label="Location"
                  value={values.serviceLogData.garageDetails.location}
                  onChange={handleChange}
                  error={Boolean(
                    touched.serviceLogData?.garageDetails?.location &&
                      errors.serviceLogData?.garageDetails?.location
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6}>
            <TextField
              {...commonTextFieldProps}
              fullWidth
              multiline
              rows={3}
              name="serviceLogData.remarks"
              label="Remarks"
              value={values.serviceLogData.remarks}
              onChange={handleChange}
            />
          </Grid>

          {/* Service Expenses */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "1rem", fontWeight: 600 }}
              >
                Service Expenses
              </Typography>
              {scheduleData &&
                Object.keys(scheduleData).length > 0 &&
                !scheduleData.isWithinRange &&
                values.serviceExpenseData.length <= 1 && (
                  <Button
                    size="small"
                    onClick={handleLoadSchedule}
                    startIcon={<AutorenewIcon />}
                    sx={{
                      ...commonButtonProps,
                      fontSize: "10px",
                    }}
                    variant="outlined"
                  >
                    Load {scheduleData.dueOnKms}KMs Schedule (
                    {scheduleData.kmsDifference}KMs away)
                  </Button>
                )}
            </Box>
            <FieldArray name="serviceExpenseData">
              {({ push, remove }) => (
                <div>
                  <TableContainer
                    component={Paper}
                    sx={{ mb: 1, border: "1px solid #e0e0e0" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Item
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Category
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Action
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Quantity
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Rate (₹)
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Amount (₹)
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            GST (%)
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", fontWeight: 600 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.serviceExpenseData.map((expense, index) => {
                          const expenseData = expense as ServiceExpenseFormData;
                          return (
                            <TableRow key={index}>
                              <TableCell sx={{ width: "20%" }}>
                                <TextField
                                  {...commonTextFieldProps}
                                  fullWidth
                                  name={`serviceExpenseData.${index}.item`}
                                  placeholder="Enter item"
                                  value={expenseData.item}
                                  onChange={handleChange}
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]?.item &&
                                      errors.serviceExpenseData?.[index]?.item
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "15%" }}>
                                <TextField
                                  {...commonTextFieldProps}
                                  fullWidth
                                  name={`serviceExpenseData.${index}.category`}
                                  placeholder="Enter category"
                                  value={expenseData.category}
                                  onChange={handleChange}
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]
                                      ?.category &&
                                      errors.serviceExpenseData?.[index]
                                        ?.category
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "15%" }}>
                                <TextField
                                  {...commonTextFieldProps}
                                  fullWidth
                                  name={`serviceExpenseData.${index}.action`}
                                  placeholder="Enter action"
                                  value={expenseData.action}
                                  onChange={handleChange}
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]
                                      ?.action &&
                                      errors.serviceExpenseData?.[index]?.action
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "5%" }}>
                                <TextField
                                  {...commonTextFieldProps}
                                  fullWidth
                                  type="number"
                                  name={`serviceExpenseData.${index}.quantity`}
                                  placeholder="Qty"
                                  value={expenseData.quantity}
                                  onChange={(e) =>
                                    handleExpenseChange(
                                      e as React.ChangeEvent<HTMLInputElement>,
                                      index
                                    )
                                  }
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]
                                      ?.quantity &&
                                      errors.serviceExpenseData?.[index]
                                        ?.quantity
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "12%" }}>
                                <AmountField
                                  name={`serviceExpenseData.${index}.rate`}
                                  placeholder="Rate"
                                  value={Number(expenseData.rate || 0)}
                                  onChange={(e) =>
                                    handleExpenseChange(e, index)
                                  }
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]?.rate &&
                                      errors.serviceExpenseData?.[index]?.rate
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "15%" }}>
                                <AmountField
                                  name={`serviceExpenseData.${index}.amount`}
                                  placeholder="Amount"
                                  value={Number(expenseData.amount || 0)}
                                  onChange={handleChange}
                                  disabled
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]
                                      ?.amount &&
                                      errors.serviceExpenseData?.[index]?.amount
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "10%" }}>
                                <AmountField
                                  name={`serviceExpenseData.${index}.gst`}
                                  placeholder="GST"
                                  value={Number(expenseData.gst || 0)}
                                  onChange={handleChange}
                                  variant="standard"
                                  error={Boolean(
                                    touched.serviceExpenseData?.[index]?.gst &&
                                      errors.serviceExpenseData?.[index]?.gst
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ width: "5%" }}>
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  sx={{
                                    color: "error.main",
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {values.serviceExpenseData.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              align="center"
                              sx={{ py: 3 }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No expenses added yet. Click "Add Expense" to
                                add one.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    size="small"
                    onClick={() => push(initialExpense)}
                    sx={{ ...commonButtonProps, fontSize: "12px" }}
                    startIcon={<AddIcon />}
                  >
                    Add Expense
                  </Button>
                </div>
              )}
            </FieldArray>
          </Grid>

          {/* Service Documents Upload */}
          <Grid item xs={12} sm={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Service Documents Upload
            </Typography>
            <DocumentUploadSection
              selectedFiles={selectedFiles}
              uploadedFiles={uploadedFiles}
              onFileSelect={onFileSelect}
              onFileRemove={onFileRemove}
              onPreview={onPreview}
              isLoading={isUploading}
            />
          </Grid>

          {/* summary auto calculate */}
          <Grid item xs={12} sm={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Summary
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                border: "1px solid",
                borderColor: "primary.light",
                bgcolor: "#F0F7FF",
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"12px"}
                >
                  Amount
                </Typography>
                <Typography variant="h6" fontSize={"14px"}>
                  ₹{expenseTotals.amount.toFixed(2)}
                </Typography>
              </div>
              <div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"12px"}
                >
                  GST
                </Typography>
                <Typography variant="h6" fontSize={"14px"}>
                  ₹{expenseTotals.gst.toFixed(2)}
                </Typography>
              </div>
              <div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"12px"}
                >
                  Total Amount (Incl. GST)
                </Typography>
                <Typography variant="h6" fontSize={"14px"} align="right">
                  ₹{expenseTotals.total.toFixed(2)}
                </Typography>
              </div>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button onClick={onCancel} {...commonButtonProps}>
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          {...commonButtonProps}
          disabled={!formikProps.dirty || isSubmitting}
        >
          {isSubmitting
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
            ? "Update"
            : "Create"}
        </Button>
      </Box>
    </Form>
  );
};

const AmountField: React.FC<{
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  variant?: "outlined" | "standard" | "filled";
}> = ({
  value,
  onChange,
  name,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  variant = "outlined",
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, "");
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value,
        },
      });
    }
  };

  return (
    <TextField
      {...commonTextFieldProps}
      fullWidth
      name={name}
      label={label}
      value={value ? Number(value).toLocaleString("en-IN") : ""}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      disabled={disabled}
      // InputProps={{
      //     startAdornment: (
      //         <Typography component="span" sx={{ mr: 1 }} fontSize="12px">
      //             ₹
      //         </Typography>
      //     ),
      // }}
      inputProps={{
        inputMode: "decimal",
      }}
    />
  );
};
